"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, ShieldAlert, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "What are the common signs of a broken or backed-up sewer line?",
  answer: "Common indicators include multiple drains backing up simultaneously, persistent sewage odors around your home or yard, gurgling sounds from toilets or sink drains, slow drainage across all fixtures, and soggy patches or unusually lush green areas on your lawn."
}, {
  id: "item-2",
  question: "How do plumbers inspect and locate sewer line damage?",
  answer: "We insert a high-definition, waterproof sewer camera directly into your sewer cleanout. The camera transmits live video footage and depth signals to our monitors, letting our technicians pinpoint exact locations of tree root intrusion, cracks, collapsed pipes, or severe blockages without digging up your property."
}, {
  id: "item-3",
  question: "What is the difference between trenchless and traditional sewer line repair?",
  answer: "Traditional repair involves digging a continuous trench to replace damaged piping, which can disrupt landscaping, driveways, and patios. Trenchless repair (such as pipe lining or pipe bursting) uses small access points to insert or rehabilitate pipes from the inside, saving time and keeping your yard intact."
}, {
  id: "item-4",
  question: "Does homeowner insurance cover emergency sewer line repairs?",
  answer: "Standard homeowner insurance policies typically do not cover wear-and-tear or tree root damage to exterior sewer lines. However, coverage is often available if you have an added water and sewer line endorsement or service line rider. We provide detailed camera reports and itemized estimates for insurance claim filings."
}, {
  id: "item-5",
  question: "How quickly can your team arrive for an emergency sewer line backup in Jonesboro?",
  answer: "Our emergency plumbing crews operate 24 hours a day, 7 days a week in Jonesboro and surrounding areas. For critical sewage backups creating safety or property hazards, our technicians typically arrive promptly with fully equipped diagnostic and hydro-jetting units to stop further damage immediately."
}];
export default function SewerFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="sewerlinerepairfaq" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <div className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium gap-1.5">
              <HelpCircle className="size-3.5 text-primary" />
              <span>Questions & Answers</span>
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight text-foreground">
            Sewer Line Repair FAQ
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans">
            Get clear, direct answers about diagnosing, fixing, and preventing sewer line emergencies in Jonesboro.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} variants={containerVariants} className="bg-card text-card-foreground border border-border rounded-xl p-4 sm:p-8 shadow-sm">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
            {faqItems.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="border border-border/80 rounded-lg px-4 sm:px-6 data-[state=open]:bg-muted/40 transition-colors">
                <AccordionTrigger className="text-base sm:text-lg font-semibold text-foreground py-4 sm:py-5 hover:no-underline hover:text-primary transition-colors text-left">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 font-sans">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="mt-12 bg-secondary text-secondary-foreground rounded-xl p-6 sm:p-8 border border-border flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="flex items-start gap-4 text-left">
            <div className="p-3 bg-background rounded-lg border border-border shrink-0">
              <ShieldAlert className="size-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold font-default text-foreground">
                Need urgent sewer line assistance?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Our 24/7 team is on call to stop backups, perform camera inspections, and restore your system.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <a href="tel:4708479076">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/contact/">
                <Wrench className="size-4" />
                <span>Request Service</span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}